import { CmsImage } from "@/components/common/CmsImage/index";
import { Subtitle } from "../../landing/Subtitle/index";
import styles from "./IFrameHero.module.css";
import type {
  InteractiveLandingPageComponent,
} from "@/types/landing";
import { useEffect, useRef } from "react";
import { logNewGoogleAnalyticsEvent } from "src/utils/utils";
import { generateSubId, mergeOfferLink } from "@/api/offers/subIdTemplate";
import { useRouter } from "next/router";
import { Offer, SanitisedOffer } from "@/types/offers";
import { getFacebookCookies } from "@/utils/cookies/client";
import { sendBeaconOrFetch } from "src/utils/apiUtils";
import { generateRandomCookieValue } from "@/utils/cookies/shared";

export const IFrameHero: InteractiveLandingPageComponent = ({
  site,
  interactiveLandingPage,
  subIdProps,
}) => {
  const iFrameContent = interactiveLandingPage?.iframe ?? "";
  const offer = interactiveLandingPage.offer?.data;
  const router = useRouter();
  const bannerflowStartFired = useRef(false);
  const { title, subtitle, heading } = interactiveLandingPage;
  const handleResize = (event: MessageEvent, sandbox: HTMLIFrameElement) => {
    if (event.data?.height > 0) {
      sandbox.style.height = `${event.data.height}px`;
    }
  };
  
  const formatForEPCReport = function(offer: Offer, subId: string, epcId: string){
    const redirectUrl = mergeOfferLink(offer.attributes.offerLink, epcId, subId);
    const merchantName = offer.attributes?.merchant?.data?.attributes.name;
    const seperator = offer.attributes?.merchant?.data?.attributes?.subIdSeperator || "_";
    const { fbp, fbc } = getFacebookCookies();

    const sanitisedOffer: SanitisedOffer = {
      id: offer.id,
      title: offer.attributes.title,
      unmergedOfferLink: offer.attributes.offerLink,
      html: '',
      offerLink: redirectUrl,
      merchant: offer.attributes.merchant,
      tagline: offer.attributes.offer_tagline?.data?.attributes?.tagline,
      deposit: offer.attributes.deposit,
      wagering: offer.attributes.wagering,
      merchantSubIdSeperator: seperator ?? "_",
      termsAndConditionsHTML: offer.attributes.termsAndConditionsMarkdown || offer.attributes.termsAndConditionsText,
      epcId,
      subId,
    };
    const allData = {
      bigQueryData: sanitisedOffer,
      index: 1,
      href: window.location.href,
      epcId,
      merchantOrderArray: [{ merchant: merchantName, position: 1 }],
      offerPageTemplateName: `Interactive Landing Page: ${merchantName}`,
      site: {
        id: site?.id,
        attributes: {
          name: site?.attributes.name,
          shortCode: site?.attributes.shortCode,
          cakeOfferId: site?.attributes.cakeOfferId,
          host: site?.attributes.host,
        },
      },
      offerLink: redirectUrl,
      userAgent: navigator.userAgent,
      fbp: fbp,
      fbc: fbc,
      clickout_location: 'Interactive Offer Redirect'
    }
    return { redirectUrl, allData };

  }

  const handleEPCReport = async (offer: Offer) => {
    if (!offer) {
      console.error('No offer provided to handleEPCReport');
      return;
    }

    const epcId = generateRandomCookieValue();
    const subId = generateSubId(subIdProps, epcId);
    const { redirectUrl, allData } = formatForEPCReport(offer, subId, epcId);

    if (site?.attributes?.googleTagManagerContainerId) {
      logNewGoogleAnalyticsEvent("interactive-offer-redirect", {
        offerLink: redirectUrl,
        epcId: epcId
      });
    }

    try {
      await sendBeaconOrFetch("/api/offerSubmit", allData, "offerSubmit");
      await router.push(redirectUrl);
    } catch (error) {
      console.error('Failed to send EPC report:', error);
      await router.push(redirectUrl);
    }
  };

  const handleMessage = async (event: MessageEvent, sandbox: HTMLIFrameElement, offer: Offer | null | undefined) => {
    if(event.origin === window.location.origin ){
      if(event.data?.action === "cta-clicked" && event.data?.type === "bannerflow-action") {
        logNewGoogleAnalyticsEvent(`bannerflow-clickout`, {
          page: window.location.href,
          merchant: offer?.attributes.merchant.data?.attributes.name || ''
        });
        if (offer) {
          handleEPCReport(offer);
        } else {
          console.error('No offer available for EPC report');
        }
      }
      if(event.data?.cta_id === "Animation_start" && event.data?.type === "bannerflow" && event.data?.action === "click") {
        if (!bannerflowStartFired.current) {
          logNewGoogleAnalyticsEvent("bannerflow-start", {
            page: window.location.href,
          });
          bannerflowStartFired.current = true;
        }
      }
      if(event.data?.cta_id === "Animation_start" && event.data?.type === "bannerflow" && event.data?.action === "animationEnd") {
        logNewGoogleAnalyticsEvent("bannerflow-end", {
          page: window.location.href,
        });
      }
      if (event.data?.type == "resize") {
        handleResize(event, sandbox);
      }
    }
  };

  useEffect(() => {
    const sandbox =
      document.querySelector<HTMLIFrameElement>(".frontend-sandbox");
    if (sandbox ) {
        sandbox.srcdoc = iFrameContent;
        sandbox.classList.add(styles.bannerflow);
        window.addEventListener(
          "message",
          (event) => {
            if(event.origin === window.location.origin)
              handleMessage(event, sandbox, offer);
          },
          { 
            "passive": true,
          }
        );
      };
  }, []);

  return (
    <div className={styles.hero}> 
      <div className={styles.content}> 
        <div className={styles.logo}>
          <CmsImage 
            cmsMedia={site?.attributes?.logo || null} 
            desiredHeight={66} 
            defaultAlt={site?.attributes?.name} 
          /> 
        </div> 
        <Subtitle 
          title={title || ""} 
          subtitle={subtitle || ""} 
          heading={heading || ""} 
        />
        <div className={styles["iframe-wrapper"]}>
          <iframe
            className="frontend-sandbox"
            title="Interactive Landing Page IFrame"
            src="about:blank"
            style={{
              outline: "none",
              border: "none",
              transform: "none",
              filter: "none",
              display: "block",
            }}
          />
        </div>
      </div> 
    </div> 
  );
};
